import { toast } from 'sonner';

import type { GapDraftView } from '@momentum/types';

import { FieldInfo } from '../FieldInfo';

/**
 * Longer than this, or carrying a newline, and a value stops being a label's
 * neighbour and becomes a paragraph — so it gets the full width under its key
 * instead of a third of the rail beside it.
 */
const INLINE_VALUE_MAX = 44;

type RawObject = { [fieldKey: string]: unknown };

function isRawObject(value: unknown): value is RawObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** What a scalar reads as, with the JSON-ish spellings kept rather than prettified. */
function scalarText(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (value === '') return '""';
  return String(value);
}

function RawRow({
  fieldKey,
  path,
  text,
  isMuted,
}: {
  fieldKey: string;
  path: string;
  text: string;
  isMuted: boolean;
}) {
  return (
    <div className="gr-raw-row" data-muted={isMuted ? 'true' : undefined}>
      <span className="gr-raw-k">
        {fieldKey}
        <FieldInfo path={path} />
      </span>
      <span className="gr-raw-v">{text}</span>
    </div>
  );
}

function RawBranch({
  fieldKey,
  path,
  childCount,
  children,
}: {
  fieldKey: string;
  path: string;
  childCount: number;
  children: React.ReactNode;
}) {
  return (
    // Open by default: "raw" means the document is on screen without asking.
    // Folding exists so a reviewer can put the parts they have read away, not
    // so they have to open twelve branches to find out what is in the record.
    <details className="gr-raw-branch" open>
      <summary>
        <span className="gr-raw-k">
          {fieldKey}
          {/* Inside the summary, so clicking the icon would also toggle the
              branch — the trigger stops the click from reaching it. */}
          <FieldInfo path={path} />
        </span>
        <span className="gr-raw-count">{childCount}</span>
      </summary>
      <div className="gr-raw-kids">{children}</div>
    </details>
  );
}

/**
 * One field, whatever shape it turned out to be.
 *
 * Deliberately generic rather than a hand-written list of the draft's fields: a
 * tab that exists to show what is actually stored has to keep showing it when
 * the persist scripts add a key, and a typed renderer would silently omit
 * exactly the new field somebody opened this tab to look for.
 */
function RawNode({
  fieldKey,
  path,
  value,
}: {
  fieldKey: string;
  path: string;
  value: unknown;
}) {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <RawRow fieldKey={fieldKey} path={path} text="[ ]" isMuted />;
    }
    return (
      <RawBranch fieldKey={fieldKey} path={path} childCount={value.length}>
        {value.map((item, itemIndex) => (
          // The index goes into the path as well as the label; `describeField`
          // collapses it, so every entry of an array shares one description.
          <RawNode
            fieldKey={String(itemIndex)}
            path={`${path}.${itemIndex}`}
            value={item}
            key={itemIndex}
          />
        ))}
      </RawBranch>
    );
  }

  if (isRawObject(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return <RawRow fieldKey={fieldKey} path={path} text="{ }" isMuted />;
    }
    return (
      <RawBranch fieldKey={fieldKey} path={path} childCount={entries.length}>
        {entries.map(([childKey, childValue]) => (
          <RawNode
            fieldKey={childKey}
            path={`${path}.${childKey}`}
            value={childValue}
            key={childKey}
          />
        ))}
      </RawBranch>
    );
  }

  if (
    typeof value === 'string' &&
    (value.length > INLINE_VALUE_MAX || value.includes('\n'))
  ) {
    return (
      <div className="gr-raw-block">
        <span className="gr-raw-k">
          {fieldKey}
          <FieldInfo path={path} />
        </span>
        {/* Whole, wrapped, with its own newlines kept: most of the long values
            here are prose an agent wrote, and a clamped or ellipsised one would
            make this tab a worse copy of the tabs beside it. */}
        <div className="gr-raw-text">{value}</div>
      </div>
    );
  }

  return (
    <RawRow
      fieldKey={fieldKey}
      path={path}
      text={scalarText(value)}
      isMuted={value === null || value === undefined || value === ''}
    />
  );
}

/**
 * Raw tab — the `gap_drafts` document itself, every field and every value.
 *
 * The other four tabs are readings of this record: they pick fields, order them
 * by what a reviewer needs first, and leave out what the pipeline stored but
 * nobody reads. That is the right default and the wrong thing entirely when the
 * question is "what does the run actually say", or when a field looks wrong and
 * the next step is a Mongo query. This tab answers with the record.
 *
 * No fetch of its own. `GET /gap-review/drafts` already returns the draft WHOLE
 * — the service stringifies the two ObjectIds, resolves employee names onto the
 * review, and drops the joined `reviewEmployees`; nothing else is projected —
 * so the queue's own payload IS the document, and a separate raw endpoint would
 * only be a second copy that could disagree with the one on screen.
 */
export function RawTab({ draft }: { draft: GapDraftView }) {
  const documentFields = Object.entries(draft as Record<string, unknown>);

  const copyDocument = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(draft, null, 2));
      toast('Copied the draft JSON', { description: draft._id });
    } catch {
      // Clipboard access is refused outside a secure context, and silently
      // doing nothing would read as the copy having worked.
      toast.error('Could not reach the clipboard');
    }
  };

  return (
    <div className="gr-raw">
      <div className="gr-raw-head">
        <p className="gr-raw-note">
          The <code>gap_drafts</code> document, as the API returned it — the
          reviewer names (<code>authorName</code>, <code>reviewerName</code>)
          are joined in rather than stored, and dates cross the wire as ISO
          strings.
        </p>
        <button className="gr-linkbtn" type="button" onClick={copyDocument}>
          Copy JSON
        </button>
      </div>

      <div className="gr-raw-tree">
        {documentFields.map(([fieldKey, fieldValue]) => (
          <RawNode
            fieldKey={fieldKey}
            path={fieldKey}
            value={fieldValue}
            key={fieldKey}
          />
        ))}
      </div>
    </div>
  );
}
